import React, { useState, useEffect } from 'react';
import {
    Card,
    Form,
    InputNumber,
    Row,
    Col,
    Typography,
    Divider,
    Alert,
    Statistic,
    Space,
    Tag,
    Button,
    Spin,
} from 'antd';
import {
    CalculatorOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    ReloadOutlined,
    ApiOutlined,
} from '@ant-design/icons';
import { LoanEligibilityConfigDTO, EligibilitySimulationRequest, EligibilitySimulationResponse } from "@/shared/api/model";
import { useSimulateLoanEligibility } from '@/shared/api/client/loan-eligibility-configuration';

const { Text } = Typography;

interface SimulatorInputs {
    savingsBalance: number;
    existingLoanBalance: number;
    productOutstandingBalance: number;
    productMaxAmount: number;
}

interface EligibilitySimulatorProps {
    config: Partial<LoanEligibilityConfigDTO>;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-TZ', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

export const EligibilitySimulator: React.FC<EligibilitySimulatorProps> = ({ config }) => {
    const [form] = Form.useForm();
    const [inputs, setInputs] = useState<SimulatorInputs>({
        savingsBalance: 5000000,
        existingLoanBalance: 0,
        productOutstandingBalance: 0,
        productMaxAmount: 10000000,
    });

    // Use the backend API for simulation
    const simulateMutation = useSimulateLoanEligibility();
    const [result, setResult] = useState<EligibilitySimulationResponse | null>(null);

    // Call the backend API when inputs or config change
    useEffect(() => {
        const request: EligibilitySimulationRequest = {
            savingsBalance: inputs.savingsBalance,
            existingLoanBalance: inputs.existingLoanBalance,
            productOutstandingBalance: inputs.productOutstandingBalance,
            productMaxAmount: inputs.productMaxAmount,
            config: config as LoanEligibilityConfigDTO,
        };

        simulateMutation.mutate(
            { data: request },
            {
                onSuccess: (response) => {
                    setResult(response);
                },
            }
        );
    }, [inputs, config]);

    const handleValuesChange = (_: any, allValues: SimulatorInputs) => {
        setInputs({
            savingsBalance: allValues.savingsBalance || 0,
            existingLoanBalance: allValues.existingLoanBalance || 0,
            productOutstandingBalance: allValues.productOutstandingBalance || 0,
            productMaxAmount: allValues.productMaxAmount || 0,
        });
    };

    const handleReset = () => {
        form.setFieldsValue({
            savingsBalance: 5000000,
            existingLoanBalance: 0,
            productOutstandingBalance: 0,
            productMaxAmount: 10000000,
        });
        setInputs({
            savingsBalance: 5000000,
            existingLoanBalance: 0,
            productOutstandingBalance: 0,
            productMaxAmount: 10000000,
        });
    };

    return (
        <Card 
            title={
                <Space>
                    <CalculatorOutlined />
                    <span>Eligibility Simulator</span>
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} onClick={handleReset} size="small">
                    Reset
                </Button>
            }
        >
            <Alert
                message="Test Your Configuration"
                description="Enter sample values below to see how the eligibility calculation behaves with the current configuration settings."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Form
                form={form}
                layout="vertical"
                initialValues={inputs}
                onValuesChange={handleValuesChange}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="savingsBalance"
                            label="Customer Savings Balance"
                            tooltip="The customer's total savings balance"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value!.replace(/,/g, '') as any}
                                min={0}
                                step={100000}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="existingLoanBalance"
                            label="Total Outstanding Loans"
                            tooltip="Customer's total outstanding loan balance across all products"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value!.replace(/,/g, '') as any}
                                min={0}
                                step={100000}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="productOutstandingBalance"
                            label="Product Outstanding Balance"
                            tooltip="Outstanding balance for the specific loan product"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value!.replace(/,/g, '') as any}
                                min={0}
                                step={100000}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="productMaxAmount"
                            label="Product Maximum Amount"
                            tooltip="Maximum loan amount allowed for this product (from ProductProperties)"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value!.replace(/,/g, '') as any}
                                min={0}
                                step={100000}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Divider orientation="left">
                <Space>
                    Calculation Result
                    <Tag color="blue" icon={<ApiOutlined />}>Backend API</Tag>
                    {simulateMutation.isPending && <Spin size="small" />}
                </Space>
            </Divider>

            {result ? (
                <>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} md={8}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic
                                    title="Final Eligible Amount"
                                    value={Number(result.finalEligibility) || 0}
                                    precision={0}
                                    valueStyle={{ 
                                        color: Number(result.finalEligibility) > 0 ? '#3f8600' : '#cf1322',
                                        fontSize: '24px'
                                    }}
                                    prefix={Number(result.finalEligibility) > 0 ? <CheckCircleOutlined /> : <WarningOutlined />}
                                    suffix="TZS"
                                    formatter={(value) => formatCurrency(Number(value))}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic
                                    title="Base Eligibility"
                                    value={Number(result.baseEligibility) || 0}
                                    precision={0}
                                    suffix="TZS"
                                    formatter={(value) => formatCurrency(Number(value))}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic
                                    title="After Product Limits"
                                    value={Number(result.afterProductLimits) || 0}
                                    precision={0}
                                    suffix="TZS"
                                    formatter={(value) => formatCurrency(Number(value))}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Divider orientation="left">Calculation Breakdown</Divider>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(result.breakdown || []).map((step, index) => (
                            <Card 
                                key={index} 
                                size="small"
                                style={{ 
                                    borderLeft: step.applied 
                                        ? '3px solid #1890ff' 
                                        : '3px solid #d9d9d9'
                                }}
                            >
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space>
                                            <Text strong>{step.step}</Text>
                                            {step.applied ? (
                                                <Tag color="blue">Applied</Tag>
                                            ) : (
                                                <Tag>Skipped</Tag>
                                            )}
                                        </Space>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {step.reason}
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Text strong style={{ fontSize: 16 }}>
                                            {formatCurrency(Number(step.value))} TZS
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </div>
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        Calculating eligibility...
                    </Text>
                </div>
            )}

            <Divider orientation="left">Active Configuration</Divider>

            <Row gutter={[8, 8]}>
                <Col>
                    <Tag color={config.considerExistingLoans ? 'green' : 'default'}>
                        Consider Existing Loans: {config.considerExistingLoans ? 'Yes' : 'No'}
                    </Tag>
                </Col>
                <Col>
                    <Tag color={config.enforceProductLimits ? 'green' : 'default'}>
                        Enforce Product Limits: {config.enforceProductLimits ? 'Yes' : 'No'}
                    </Tag>
                </Col>
                <Col>
                    <Tag color={config.productSpecificBalanceOnly ? 'green' : 'default'}>
                        Product-Specific Balance: {config.productSpecificBalanceOnly ? 'Yes' : 'No'}
                    </Tag>
                </Col>
                <Col>
                    <Tag color={config.applyOneThirdRule ? 'green' : 'default'}>
                        1/3 Rule: {config.applyOneThirdRule ? `Yes (${config.oneThirdRuleMultiplier || 0.33})` : 'No'}
                    </Tag>
                </Col>
                <Col>
                    <Tag>
                        Default Multiplier: {config.defaultSavingsMultiplier || 3}x
                    </Tag>
                </Col>
                <Col>
                    <Tag>
                        High Balance Multiplier: {config.highBalanceMultiplier || 2}x
                    </Tag>
                </Col>
            </Row>
        </Card>
    );
};

export default EligibilitySimulator;
